import type { DspModuleDescriptor, DspPortDescriptor } from '../../dsp-module-contract/src/index.js';

export interface PatchEndpoint { readonly nodeId: string; readonly portId: string }
export interface PatchPosition { readonly x: number; readonly y: number }
export interface PatchNodeV1 {
  readonly id: string;
  readonly moduleId: string;
  readonly parameters: Readonly<Record<string, number>>;
  readonly position?: PatchPosition;
}
export interface PatchConnectionV1 {
  readonly id: string;
  readonly source: PatchEndpoint;
  readonly target: PatchEndpoint;
  readonly feedback?: boolean;
}
export interface PatchGraphV1 {
  readonly schemaVersion: 1;
  readonly id: string;
  readonly seed: number;
  readonly nodes: readonly PatchNodeV1[];
  readonly connections: readonly PatchConnectionV1[];
}
export interface ValidatedPatchGraph {
  readonly graph: PatchGraphV1;
  readonly executionOrder: readonly string[];
}

const ID = /^[a-z][a-z0-9-]*$/;
const byId = <T extends { readonly id: string }>(items: readonly T[], kind: string): Map<string, T> => {
  const result = new Map<string, T>();
  for (const item of items) {
    if (!ID.test(item.id)) throw new RangeError(`${kind} id is invalid: ${item.id}`);
    if (result.has(item.id)) throw new RangeError(`duplicate ${kind}: ${item.id}`);
    result.set(item.id, item);
  }
  return result;
};
const port = (ports: readonly DspPortDescriptor[], id: string, label: string): DspPortDescriptor => {
  const found = ports.find((candidate) => candidate.id === id);
  if (!found) throw new RangeError(`${label} port does not exist: ${id}`);
  return found;
};

export const validatePatchGraph = (
  graph: PatchGraphV1,
  descriptors: readonly DspModuleDescriptor[],
): ValidatedPatchGraph => {
  if (graph.schemaVersion !== 1) throw new RangeError('unsupported schemaVersion');
  if (!ID.test(graph.id)) throw new RangeError('patch id is invalid');
  if (!Number.isSafeInteger(graph.seed)) throw new RangeError('seed must be a safe integer');
  const modules = new Map(descriptors.map((descriptor) => [descriptor.id, descriptor]));
  if (modules.size !== descriptors.length) throw new RangeError('duplicate module descriptor');
  const nodes = byId(graph.nodes, 'node');
  const connections = byId(graph.connections, 'connection');
  void connections;

  for (const node of graph.nodes) {
    const descriptor = modules.get(node.moduleId);
    if (!descriptor) throw new RangeError(`unknown module: ${node.moduleId}`);
    const parameterMap = new Map(descriptor.parameters.map((parameter) => [parameter.id, parameter]));
    for (const [id, value] of Object.entries(node.parameters)) {
      const parameter = parameterMap.get(id);
      if (!parameter) throw new RangeError(`unknown parameter ${id} on node ${node.id}`);
      if (!Number.isFinite(value) || value < parameter.minValue || value > parameter.maxValue) {
        throw new RangeError(`parameter ${id} is outside its range`);
      }
    }
    if (node.position && (!Number.isFinite(node.position.x) || !Number.isFinite(node.position.y))) {
      throw new RangeError(`node ${node.id} position must be finite`);
    }
  }

  const outgoing = new Map(graph.nodes.map((node) => [node.id, [] as string[]]));
  const indegree = new Map(graph.nodes.map((node) => [node.id, 0]));
  const occupiedInputs = new Set<string>();
  for (const connection of graph.connections) {
    const sourceNode = nodes.get(connection.source.nodeId);
    const targetNode = nodes.get(connection.target.nodeId);
    if (!sourceNode) throw new RangeError(`missing source node: ${connection.source.nodeId}`);
    if (!targetNode) throw new RangeError(`missing target node: ${connection.target.nodeId}`);
    const sourceModule = modules.get(sourceNode.moduleId)!;
    const targetModule = modules.get(targetNode.moduleId)!;
    const sourcePort = port(sourceModule.outputs, connection.source.portId, 'source');
    const targetPort = port(targetModule.inputs, connection.target.portId, 'target');
    if (sourcePort.kind !== targetPort.kind || sourcePort.channels !== targetPort.channels) {
      throw new RangeError(`incompatible ports in connection ${connection.id}`);
    }
    const inputKey = `${targetNode.id}:${targetPort.id}`;
    if (occupiedInputs.has(inputKey)) throw new RangeError(`duplicate connection to input ${inputKey}`);
    occupiedInputs.add(inputKey);
    if (connection.feedback) {
      if (targetModule.id !== 'mir.delay') throw new RangeError('feedback connection must enter an explicit delay module');
      continue;
    }
    outgoing.get(sourceNode.id)!.push(targetNode.id);
    indegree.set(targetNode.id, indegree.get(targetNode.id)! + 1);
  }

  const queue = graph.nodes.map((node) => node.id).filter((id) => indegree.get(id) === 0).sort();
  const order: string[] = [];
  while (queue.length) {
    const current = queue.shift()!;
    order.push(current);
    for (const target of outgoing.get(current)!) {
      const next = indegree.get(target)! - 1;
      indegree.set(target, next);
      if (next === 0) { queue.push(target); queue.sort(); }
    }
  }
  if (order.length !== graph.nodes.length) throw new RangeError('graph contains a cycle');
  return Object.freeze({ graph, executionOrder: Object.freeze(order) });
};
